import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Megaphone } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/rbac";
import { SchoolSelect, useSchools, ALL_SCHOOLS } from "../components/SchoolSelect";

const EMPTY = { title: "", body: "", audience: "all", meeting_url: "", meeting_platform: "" };

export default function AnnouncementsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canCreate = can.createAnnouncement(user);
  const { schools, schoolId, setSchoolId, isSuper, queryId } = useSchools({ allowAll: true });
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [defaults, setDefaults] = useState({ meeting_url: "", meeting_platform: "" });

  useEffect(() => {
    api.get("/settings/public").then(r => setDefaults({
      meeting_url: r.data?.default_meeting_url || "",
      meeting_platform: r.data?.default_meeting_platform || "",
    })).catch(() => {});
  }, []);
  const load = () => {
    const qs = queryId ? `?school_id=${queryId}` : "";
    api.get(`/announcements${qs}`).then(r => setItems(r.data || []));
  };
  useEffect(() => { load(); }, [queryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, meeting_url: defaults.meeting_url, meeting_platform: defaults.meeting_platform });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      title: it.title || "",
      body: it.body || "",
      audience: it.audience || "all",
      meeting_url: it.meeting_url || "",
      meeting_platform: it.meeting_platform || "",
    });
    setOpen(true);
  };
  const submit = async () => {
    try {
      // When "all schools" selected, require an actual school (creation is per-school)
      const targetSchool = queryId || schools[0]?.id;
      if (!targetSchool) { toast.error(t("select_school")); return; }
      const payload = { ...form, school_id: targetSchool };
      if (editing) await api.put(`/announcements/${editing.id}`, payload);
      else await api.post("/announcements", payload);
      toast.success(t("success")); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const remove = async (it) => {
    try {
      await api.delete(`/announcements/${it.id}`);
      toast.success(t("success")); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <h1 className="text-3xl font-semibold">{t("announcements")}</h1>
        <div className="flex gap-3">
          <SchoolSelect
            schools={schools}
            value={schoolId}
            onChange={setSchoolId}
            testId="ann-school-select"
            disabled={!isSuper}
            showAll={isSuper}
          />
          {canCreate && <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} disabled={schoolId === ALL_SCHOOLS ? schools.length === 0 : !schoolId} data-testid="new-ann-btn"><Plus className="w-4 h-4 me-2"/>{t("new_announcement")}</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>{editing ? t("edit") : t("new_announcement")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("title")}</Label><Input value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="ann-title"/></div>
                <div><Label>{t("body")}</Label><Textarea rows={4} value={form.body} onChange={e=>setForm({...form, body:e.target.value})} data-testid="ann-body"/></div>
                <div><Label>{t("audience")}</Label>
                  <Select value={form.audience} onValueChange={v=>setForm({...form, audience:v})}><SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="all">{t("all")}</SelectItem><SelectItem value="teachers">{t("teachers")}</SelectItem><SelectItem value="parents">{t("parent")}</SelectItem><SelectItem value="students">{t("students")}</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2"><Label>{t("meeting_url")}</Label><Input value={form.meeting_url} onChange={e=>setForm({...form, meeting_url:e.target.value})} placeholder={defaults.meeting_url || "https://meet.google.com/..."} data-testid="ann-meeting-url"/></div>
                  <div><Label>{t("meeting_platform")}</Label>
                    <Select value={form.meeting_platform || ""} onValueChange={v=>setForm({...form, meeting_platform:v})}><SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                      <SelectContent><SelectItem value="zoom">Zoom</SelectItem><SelectItem value="teams">Teams</SelectItem><SelectItem value="meet">Meet</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                {defaults.meeting_url && (
                  <button type="button" className="text-xs text-primary underline" onClick={() => setForm({...form, meeting_url: defaults.meeting_url, meeting_platform: defaults.meeting_platform})} data-testid="ann-use-default-meeting">
                    {t("use_default_meeting")}
                  </button>
                )}
                <Button onClick={submit} className="w-full" data-testid="ann-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      <div className="space-y-3">
        {items.map(a => (
          <Card key={a.id} className="p-5 border shadow-none flex gap-4" data-testid={`ann-${a.id}`}>
            <div className="w-10 h-10 rounded-lg bg-secondary/15 text-secondary grid place-items-center"><Megaphone className="w-5 h-5"/></div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between gap-2">
                <div className="font-semibold">{a.title}</div>
                <div className="flex items-center gap-2">
                  <div className="text-xs text-muted-foreground font-mono whitespace-nowrap">{a.at?.slice(0,10)}</div>
                  {canCreate && <RowActions item={a} onEdit={openEdit} onDelete={remove} testIdPrefix="ann" />}
                </div>
              </div>
              <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{a.body}</div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="inline-block text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">{a.audience}</span>
                {a.meeting_url && (
                  <a href={a.meeting_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline" data-testid={`ann-join-${a.id}`}>
                    🔗 Join {a.meeting_platform || "online"}
                  </a>
                )}
              </div>
            </div>
          </Card>
        ))}
        {items.length===0 && <Card className="p-8 text-center text-muted-foreground border-dashed">{t("no_data")}</Card>}
      </div>
    </div>
  );
}
